import React, { useState, useEffect, useContext } from 'react';
import {IProps, IState} from './types';
import Button from '@components/Button';
import actions from '@state/actions';
import { AuthContext } from '@utils/DOIgnition/AuthContext';
import { useDispatch } from 'react-redux';
import { useSelector } from 'react-redux';
import selectors from '@state/selectors';
import TemplateStatus from '../TemplateStatus';
import TemplateOrganization from '../TemplateOrganization';
import TemplateName from '@routes/private/Templates/components/NewTemplate/components/TemplateName';
import Tags from '@routes/private/Templates/components/NewTemplate/components/Tags';
import * as S from './styles';
import C from '@assets/themes/colors';
import { getBrandIdByOEMName } from 'utils/editor';
import { cloneDeep } from 'lodash';
import { ITemplate } from '@state/templates/types';
import { IUserType } from '@state/auth/types';

const EditTemplate:React.FC<IProps> = (props) => {

  const authContext = useContext(AuthContext);
  const dispatch = useDispatch();
  const [isChecking, setIsChecking] = useState(false);
  const [tipLabel, setTipLabel] = useState("");
  const [tagErrors, setTagErrors] = useState(false);
  const [hasChanged, setHasChanged] = useState(0);
  const [templateStatus, setTemplateStatus] = useState("");
  const dsConfig = useSelector(selectors.dsConfig.getDSConfig);

  const getTemplateByName = (name: string) => {
    let query=`?filter=c.name=%27${encodeURIComponent(name)}%27 and c.templateStatus!=%27INACTIVE%27&orderby=c.name&start=0&pagelength=1`;
    return new Promise((resolve, reject) => {
      dispatch(actions.templates.getTemplate(query, (result: any, error: any) => {
        if (error) {
          reject(error);
        } else {
          resolve(result);
        }
      }))
    })
  } 

  const validateTemplateNames = async (value: string) => {
    const repeatedTemplate :any = await getTemplateByName(value);
    setIsChecking(false);
    if (
      repeatedTemplate && repeatedTemplate.length > 0
    ) {
      setTipLabel("ERROR: Two templates cannot share the same name ");
    } else {
      if (value === "")
      setTipLabel("ERROR: The template name cannot be an empty value")
      else
      setTipLabel("");
    }
  }

  useEffect(() => {
    if (props.files[0].name !== props.files[0]?.file?.name) {
      setIsChecking(true);
      setHasChanged(hasChanged + 1);
      validateTemplateNames(props.files[0].file.name);
      setTipLabel("Checking template name...");
    } else {
      setIsChecking(false);
      setTipLabel("");
    }
  }, [props.files[0]?.file?.name])

  useEffect(() => {
    if (props.files[0]?.tags) {
      setHasChanged(hasChanged + 1);
      if (
        !(props.files[0]?.tags.designType) || !(props.files[0]?.tags.platform) || 
        (props.files[0]?.tags.formats && props.files[0].tags.formats.length === 0) ||
        !(props.files[0]?.tags.formats) || !(props.files[0]?.tags.oem)
      ) setTagErrors(true);
      else setTagErrors(false);
    }
  }, [props.files[0]?.tags])

  useEffect(() => {
    if (props.files[0]?.templateStatus) {
      setTemplateStatus(props.files[0].templateStatus);
    }
  }, [])

  useEffect(() => {
    if (props.files[0]?.templateStatus) {
      setHasChanged(hasChanged + 1);
    }
  }, [props.files[0]?.templateStatus])

  const handleSave = () => {
    if (tipLabel === "" && tagErrors === false && hasChanged > 1 && !props.isSaving) {
      const newTemplates: ITemplate[] = [];
      props.files.forEach(file => {
        let template = cloneDeep(file);
        template.name = file.file.name;
        const brandId: string = getBrandIdByOEMName(file.tags.oem, dsConfig);
        template.tags.oem = brandId;
        const userData = {
          email: authContext?.userData.profile.email ? authContext?.userData.profile.email : "defaultuser@tangocode.com",
          name: authContext?.userData.profile.name ? authContext?.userData.profile.name : "Default User"
        };
        file.updatedBy = userData;
        if (templateStatus !== file.templateStatus && file.templateStatus !== 'DISABLED') {
          const newPublishedDate = {
            user: userData,
            date: new Date(),
            action: file.templateStatus
          };
          if (file.publishedInfo) file.publishedInfo = [...file.publishedInfo, newPublishedDate]
          else file.publishedInfo = [newPublishedDate];
        }
        newTemplates.push(template);
      });
      props.handleEditTemplates(newTemplates);
    }
  }

  return (
    <S.Root style={{
      display: props.open ? 'flex' : 'none',
    }} >
      <S.BoxContainer>
        <S.TitleContainer>
          <S.LeftSide>
            <S.Title>
              {props.files.length < 2 ? props.files[0].name : `${props.files.length} Templates`}
            </S.Title>
            <S.SubtitleContainer>
              <S.Subtitle>
                { 
                  props.files.length < 2 ? `Created by ${props.files[0].createdBy?.name ? props.files[0].createdBy.name : "Deafault User"}, ${
                    new Intl.DateTimeFormat('en-US',{day:'2-digit', month:'2-digit', year:'2-digit'}).format(new Date(props.files[0].createdDate))}` :
                    `All current tags and statuses will be overridden once you save these changes`
                }
              </S.Subtitle>
              { 
                props.files.length < 2 &&
                <S.Hour>
                  {new Intl.DateTimeFormat('en-US',{hour:'2-digit', minute:'2-digit'}).format(new Date(props.files[0].createdDate))}
                </S.Hour>
              }
            </S.SubtitleContainer>
          </S.LeftSide>
          {
            props.files.length < 2 &&
            <S.RightSide
              labelColor={
                templateStatus === "UNPUBLISHED" ? "#f5bc00" : 
                templateStatus === "PUBLISHED" ? "#5ed485" : "#c4c4c4"
              }
            >
              {
                templateStatus === "UNPUBLISHED" ? "Unpublished" : 
                templateStatus === "PUBLISHED" ? "Published" :
                templateStatus === "DISABLED" ? "Disabled" : ""
              }
            </S.RightSide>
          }
        </S.TitleContainer>
        <S.Content>
          {
            props.files.length < 2 &&
            <TemplateName 
              files={props.files} 
              setFiles={props.setFiles} 
              currentIndex={0} 
              tipLabel={tipLabel}
              tipLabelColor={isChecking ? '#8891a0' : C.RED}
              fromEditTemplate={true}
            />
          }
          <S.TopContent>
            <TemplateStatus
              files={props.files} 
              setFiles={props.setFiles} 
            />
            {
              authContext?.userData?.profile.role === IUserType.DEALER_USER &&
              <TemplateOrganization
                files={props.files} 
                setFiles={props.setFiles}
                currentIndex={0}
                fromEditTemplate={true}
              />
            }
          </S.TopContent>
          { props.files.length < 2 &&
          <Tags 
            files={props.files} 
            setFiles={props.setFiles} 
            currentIndex={0} 
            fromEditTemplate={true}
          />
          }
        </S.Content>
        <S.ActionsContainer>
          <S.Action>
            <Button
              label="CANCEL"
              style={{
                width: '112px',
                marginRight: 12, 
                backgroundColor: "transparent", 
                border: `1px solid ${C.DARK_ORANGE}`, 
                hoverBackgroundColor: `${C.DARK_ORANGE}22`,
              }}
              labelStyle={{color: C.DARK_ORANGE}}
              onClick={() => {if (!props.isSaving) props.setOpen(false)}}
            />
          </S.Action>
          <S.Action>
            <Button
              label={props.isSaving ? "SAVING..." : "SAVE"}
              style={{
                width: '112px',
                backgroundColor: C.DARK_ORANGE, 
                flexGrow: 0, 
                hoverBackgroundColor: `${C.DARK_ORANGE}AA`,
              }} 
              onClick={handleSave}
              disabled={!(tipLabel === "" && tagErrors === false && hasChanged > 1)}
            />
          </S.Action>
        </S.ActionsContainer>
      </S.BoxContainer>
    </S.Root>
  );
}

export default EditTemplate;